import type {
    AnyObject,
    ArgumentsSchema,
    AutoLayout,
    BackgroundProtocol,
    BoolValue,
    Border,
    DateValue,
    DeleteRecordActionConfig,
    EndArg,
    FilterFormType,
    NodeFieldContent,
    NullValue,
    NumberValue,
    OpenPageActionConfig,
    RecordLikeProtocol,
    RichTextContentProtocol,
    RichTextValue,
    SourceRecordConfig,
    StringArrayValue,
    StripePayConfig,
    SubProcessArg,
    SubProcessResolvedArg,
    TextValue,
    TypeInstanceMap,
    VariableADTvalue,
    WechatFollowWaybillConfig,
    WechatGetOrderListConfig,
    WechatGetOrderShippingConfig,
    WechatPayConfig,
    WechatProfitSharingConfig,
    WechatProfitSharingPlatformEnum,
    WechatProfitSharingQueryAmountConfig,
    WechatProfitSharingQueryConfig,
    WechatProfitSharingRelationTypeEnum,
    WechatProfitSharingTypeEnum,
    WechatRefundConfig,
    WechatRefundQueryConfig,
    WechatTemplateMsgConfig,
    WechatUploadShippingInfoConfig
} from '@lighthouse/core'
import type { JSONContent } from '@tiptap/core'
import type { Node } from '@xyflow/react'
import type { HTMLAttributeAnchorTarget } from 'react'
import type { MakeADT } from 'ts-adt/MakeADT'

import type { ApproverUser, EmailParticipant } from './common'
import type { SenderAccountType } from './node'
import type { AiPlatformType, OtherPlatformType } from './otherPlatform'
import type { DataSourceRelayConfig, SettingRecordFields } from './record'

export type FieldPermission = {
    fieldId: string
    mode: 'READ' | 'WRITE' | 'NONE'
}

export enum EMAIL_EDIT_TYPE {
    RICHTEXT = 'RICHTEXT',
    HTML = 'HTML'
}

export enum PARAGRAPH_TYPE {
    NORMAL = 'NORMAL',
    MULTIPLE = 'MULTIPLE'
}

export enum PARAGRAPH_WIDTH_TYPE {
    NORMAL = 'NORMAL', // 标准
    WIDER = 'WIDER', // 较宽
    FULL = 'FULL' // 全宽
}

export enum SearchApiResultType {
    TEXT = 'TEXT',
    LINK = 'LINK'
}

export type ExtractParams = {
    id: string
    name: string
    innerType: TypeInstanceMap
    description: string
    required: boolean
}

export type RunCodeParam = {
    id: string
    name: string
    isEdit?: boolean
    value?: VariableADTvalue
}

export type RunCodeResult = {
    id: string
    name: string
    innerType: TypeInstanceMap
}

export enum LoopTypeEnum {
    BASE_ON_TIMES = 'BASE_ON_TIMES',
    BASE_ON_RECORDS = 'BASE_ON_RECORDS'
}

export enum OpenDetailPageSelectedRecordType {
    CURRENT_RECORD = 'CURRENT_RECORD',
    MATCHED_RECORD = 'MATCHED_RECORD',
    UPSTREAM_RECORD = 'UPSTREAM_RECORD'
}

// 触发
export enum TriggerNodeType {
    CREATE_RECORD_TRIGGER = 'CREATE_RECORD_TRIGGER',
    UPDATE_RECORD_TRIGGER = 'UPDATE_RECORD_TRIGGER',
    MATCH_CONDITION_TRIGGER = 'MATCH_CONDITION_TRIGGER',
    SCHEDULED_TRIGGER = 'SCHEDULED_TRIGGER',
    CLICK_TRIGGER = 'CLICK_TRIGGER',
    // MANUALLY_TRIGGER = 'MANUALLY_TRIGGER',
    // 子流程触发
    ARG_TRIGGER = 'ARG_TRIGGER',

    // 触发节点 空的节点类型
    EMPTY_TRIGGER = 'EMPTY_TRIGGER',

    WEBSOCKET_TRIGGER = 'WEBSOCKET_TRIGGER',

    WEBHOOK_TRIGGER = 'WEBHOOK_TRIGGER'
}

// 流程控制
export enum FlowControlNodeType {
    LOOP_ACTION = 'LOOP_ACTION',
    HANDLE_SUB_PROCESS = 'HANDLE_SUB_PROCESS',
    CONDITION = 'CONDITION',
    SEND_WEBSOCKET = 'SEND_WEBSOCKET'
}

// 工具
export enum OtherPlatformNodeType {
    WX_ROBOT_ACTION = 'WX_ROBOT_ACTION',
    DINGTALK_ROBOT_ACTION = 'DINGTALK_ROBOT_ACTION',
    ALIYUN_MESSAGE_ACTION = 'ALIYUN_MESSAGE_ACTION',
    WECHAT_TEMPLATE_MSG_ACTION = 'WECHAT_TEMPLATE_MSG_ACTION',
    WECHAT_SUBSCRIPTION_MSG_ACTION = 'WECHAT_SUBSCRIPTION_MSG_ACTION',
    WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION = 'WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION',
    WECHAT_OPEN_LOCATION_ACTION = 'WECHAT_OPEN_LOCATION_ACTION',
    OPEN_WECHAT_CUSTOMER_SERVICE = 'OPEN_WECHAT_CUSTOMER_SERVICE',
    // 微信商户号
    WECHAT_PAY = 'WECHAT_PAY',
    WECHAT_REFUND = 'WECHAT_REFUND',
    WECHAT_REFUND_QUERY = 'WECHAT_REFUND_QUERY',
    WECHAT_PROFIT_SHARING = 'WECHAT_PROFIT_SHARING',
    WECHAT_PROFIT_SHARING_QUERY = 'WECHAT_PROFIT_SHARING_QUERY',
    WECHAT_PROFIT_SHARING_QUERY_AMOUNT = 'WECHAT_PROFIT_SHARING_QUERY_AMOUNT',
    WECHAT_ADD_RECEIVER = 'WECHAT_ADD_RECEIVER',
    WECHAT_DELETE_RECEIVER = 'WECHAT_DELETE_RECEIVER',
    // 小程序
    WECHAT_NAVIGATE_TO_MINI_PROGRAM = 'WECHAT_NAVIGATE_TO_MINI_PROGRAM',
    WECHAT_OPEN_AUDIO_PLAYER = 'WECHAT_OPEN_AUDIO_PLAYER',
    STRIPE = 'STRIPE',
    SEND_EMAIL_ACTION = 'SEND_EMAIL_ACTION',
    WECHAT_OPEN_CHANNELS_ACTIVITY = 'WECHAT_OPEN_CHANNELS_ACTIVITY',
    WECHAT_GENERATE_MINIPROGRAM_CODE = 'WECHAT_GENERATE_MINIPROGRAM_CODE',
    WECHAT_UPLOAD_SHIPPING_INFO = 'WECHAT_UPLOAD_SHIPPING_INFO',
    WECHAT_GET_ORDER_SHIPPING = 'WECHAT_GET_ORDER_SHIPPING',
    WECHAT_GET_ORDER_LIST = 'WECHAT_GET_ORDER_LIST',
    WECHAT_FOLLOW_WAYBILL = 'WECHAT_FOLLOW_WAYBILL',

    // Search API
    GOOGLE_SEARCH_ACTION = 'GOOGLE_SEARCH_ACTION',
    BAIDU_SEARCH_ACTION = 'BAIDU_SEARCH_ACTION',
    BING_SEARCH_ACTION = 'BING_SEARCH_ACTION'
}

// AI
export enum AINodeType {
    LLM_ACTION = 'LLM_ACTION',
    PARAM_EXTRACT_ACTION = 'PARAM_EXTRACT_ACTION',
    CODE_ACTION = 'CODE_ACTION'
}

export enum NoticeNodeType {
    STATION_MESSAGE_ACTION = 'STATION_MESSAGE_ACTION'
}

export enum ActionDataNodeType {
    CREATE_SINGLE_RECORD_ACTION = 'CREATE_SINGLE_RECORD_ACTION',
    CREATE_RECORD_ACTION = 'CREATE_RECORD_ACTION',
    UPDATE_RECORD_ACTION = 'UPDATE_RECORD_ACTION',
    FIND_SINGLE_RECORD_ACTION = 'FIND_SINGLE_RECORD_ACTION',
    FIND_RECORD_ACTION = 'FIND_RECORD_ACTION',
    DELETE_RECORD = 'DELETE_RECORD',
    IMPORT_EXCEL = 'IMPORT_EXCEL',
    BATCH_IMPORT_FILE = 'BATCH_IMPORT_FILE'
}

export enum OperatorNodeType {
    OPEN_PAGE = 'OPEN_PAGE',
    OPEN_LINK = 'OPEN_LINK',
    GO_BACK = 'GO_BACK',
    NOTIFICATION = 'NOTIFICATION',
    COPY = 'COPY',
    DOWNLOAD_FILE = 'DOWNLOAD_FILE',
    CALL = 'CALL',
    REFRESH_PAGE = 'REFRESH_PAGE',
    CLOSE_FLOAT = 'CLOSE_FLOAT',
    REFRESH_USER_PERMISSIONS = 'REFRESH_USER_PERMISSIONS'
}

export enum EndNodeType {
    END = 'END'
}

export type NodeTypes =
    | TriggerNodeType
    | FlowControlNodeType
    | OtherPlatformNodeType
    | ActionDataNodeType
    | NoticeNodeType
    | OperatorNodeType
    | AINodeType
    | EndNodeType

// // --------- config -------
export enum NodeAndEdgeStatusEnum {
    NOT_START = 'NOT_START',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED',
    RUNNING = 'RUNNING',
    PENDING = 'PENDING',
    INTERRUPTED = 'INTERRUPTED'
}

export type CreateRecordTriggerConfig = DataSourceRelayConfig

export type UpdateRecordTriggerConfig = DataSourceRelayConfig & {
    watchedFields: string[]
}

export type MatchConditionTriggerConfig = DataSourceRelayConfig & {
    filter: FilterFormType
}

export type InitiateApproveTriggerConfig = DataSourceRelayConfig & {
    stateFieldId: string // 绑定的状态字段id,
    pageId: string // 表单所在的页面id
    formModuleId: string // 表单容器 Id
}

// 子流程触发节点
export type VariableTriggerConfig = {
    args: ArgumentsSchema[]
}

export type ScheduledTriggerRule = 'ONCE' | 'MINUTE' | 'HOUR' | 'DAY' | 'WEEKDAY' | 'WEEK' | 'MONTH'

export type ClickTriggerConfig = {
    args: ArgumentsSchema[]
}

export type EndConfig = {
    outputArgs?: EndArg[]
}

export type WebsocketTriggerConfig = {
    url: string
    eventName: string
    eventValue: string
    args: ArgumentsSchema[]
}

export type WebhookTriggerConfig = {
    url: string
}

export type ScheduledTriggerConfig = {
    scheduleStartTime: Date | undefined
    scheduleEndTime: Date | undefined
    scheduleRule: ScheduledTriggerRule
    /** 只触发一次，指定时间 */
    onceTime?: Date
    /** 分规则 1~60 */
    scheduleMinuteRule?: string
    /** 时规则 1~24 */
    scheduleHourRule?: string
    /** 天规则 */
    scheduleDayRule?: {
        /** 1~31 */
        day: string
        /** 0~24，间隔 15 分钟，例：00:15 */
        time: string
    }
    /** 工作日规则 */
    /** 0~24，间隔 15 分钟，例：00:15 */
    scheduleWeekdayRule?: string
    /** 周规则 */
    scheduleWeekRule?: {
        /** 1~52 */
        week: string
        /** 0~6，表示一~日 */
        days: string[]
        /** 0~24，间隔 15 分钟，例：00:15 */
        time: string
    }
    /** 月规则 */
    scheduleMonthRule?: {
        /** 1~12 */
        month: string
        /** 1~31 */
        days: string[]
        /** 0~24，间隔 15 分钟，例：00:15 */
        time: string
    }
}

// export type ManuallyTriggerConfig = Record<string, object>

// 创建单条记录节点
export type CreateSingleRecordActionConfig = SettingRecordFields
// 创建多条记录节点
export type CreateRecordActionConfig = SettingRecordFields & {
    nodeId: string
}

export type UpdateRecordActionConfig = SourceRecordConfig & {
    fields: NodeFieldContent[]
}

// 查询单条记录节点
export type FindSingleRecordActionConfig = SourceRecordConfig

// 查询多条记录节点
export type FindRecordActionConfig = SourceRecordConfig

export type ImportExcelImportMode = 'skip' | 'cover' | 'update' | 'error'

export type ImportExcelActionConfig = {
    /** 目标数据表 ID */
    dataSourceId: string
    /**
     * Excel 文件来源（富文本结构，支持变量解析）
     * 说明：当为数组时，取第一个元素作为下载地址
     */
    excelSource: RichTextContentProtocol
    /** 是否识别重复项 */
    repeat: boolean
    /** 识别重复依据的字段 ID 列表 */
    compareFields: string[]
    /** 必填字段 ID 列表（缺失或为空则该行导入失败） */
    requiredFields: string[]
    /**
     * 识别到重复记录时的处理方式（仅 repeat=true 时生效）
     * - skip: 跳过重复记录
     * - cover: 覆盖重复记录（更新已有记录并新增不存在的记录）
     * - update: 仅更新重复记录（不新增）
     * - error: 导入报错（整个节点失败，不导入任何数据）
     */
    importMode?: ImportExcelImportMode
}

export type BatchImportFileImportMode = 'IMAGES' | 'ARCHIVE'

export type BatchImportFileActionConfig = {
    /** 目标数据表 ID */
    dataSourceId: string
    /** 目标附件字段 ID */
    attachmentFieldId: string
    /** 目标映射字段 ID */
    mappingFieldId: string
    /** 导入模式：IMAGES-多张图片, ARCHIVE-压缩包(zip/rar) */
    importMode: BatchImportFileImportMode
    /** 文件来源（变量数组） */
    fileSource?: VariableADTvalue
}

export enum LoopErrorHandlerEnum {
    'CONTINUE' = 'CONTINUE',
    'BREAK' = 'BREAK',
    'RETURN' = 'RETURN'
}

export type LoopTimesConfig = {
    start: VariableADTvalue
    end: VariableADTvalue
    step: VariableADTvalue
    subProcessId: string
    args: SubProcessArg[]
}

export type LoopRecordsConfig = {
    nodeId: string
    subProcessId: string
    args: SubProcessArg[]
}
// 循环节点
export type LoopNodeActionConfig = MakeADT<
    'loopType',
    {
        BASE_ON_TIMES: {
            timesConfig: Partial<LoopTimesConfig>
            loopErrorHandler: LoopErrorHandlerEnum
        }
        BASE_ON_RECORDS: {
            recordsConfig: Partial<LoopRecordsConfig>
            loopErrorHandler: LoopErrorHandlerEnum
        }
    }
>

// CREATE_RECORD_TRIGGER: NodeProtocol & { config?: CreateRecordTriggerConfig }
// UPDATE_RECORD_TRIGGER: NodeProtocol & { config?: UpdateRecordTriggerConfig }

export type InitiateApproveActionConfig = {
    approveObjectId: string
    approveFlowId: string
}

export type CarbonCopyActionConfig = {
    users: ApproverUser[]
}

// export type FindRecordActionConfig = Record<string, object>

export type FilterGroupItem = {
    id: string
    filter: FilterFormType
}

export enum ConditionRunType {
    'PARALLEL' = 'PARALLEL',
    'UNIQUE' = 'UNIQUE'
}

export type ConditionConfig = {
    filterGroup: FilterGroupItem[]
    elseHandleId: string
    runType: ConditionRunType
}

export type SendMsgWxRootConfigMsgType = 'text'

export type SendMsgWxRootConfig = {
    id: string
    msgtype: SendMsgWxRootConfigMsgType
    webhook: string
    text: {
        // content: string
        editorContent: RichTextContentProtocol
        mentionedContent: RichTextContentProtocol
    }
}

export type WechatSubscriptionMessageConfig = {
    templateIds: string[]
}

export type WechatSubscriptionMessageNotificationsConfig = {
    personList: VariableADTvalue[]
    templateId: string
    values: { variableName: string; value: JSONContent }[]
    page: {
        openPageId: string
        pathParameters?: { id: string; value?: RichTextContentProtocol }[]
        queryParameters?: { id: string; value?: RichTextContentProtocol }[]
    }
}

export type WechatOpenLocationActionConfig = {
    latitude: RichTextContentProtocol
    longitude: RichTextContentProtocol
    name: RichTextContentProtocol
    address: RichTextContentProtocol
    scale: RichTextContentProtocol
    // infoUrl: RichTextContentProtocol
}

export type WechatNavigateToMiniProgramActionConfig = {
    appId: RichTextContentProtocol
    path: RichTextContentProtocol
}

export type WechatOpenChannelsActivityActionConfig = {
    finderUserName: RichTextContentProtocol
    feedId: RichTextContentProtocol
}

export type WechatGenerateMiniProgramCodeActionConfig = {
    path: RichTextContentProtocol
    scene: RichTextContentProtocol
    width?: RichTextContentProtocol
}

export type WechatOpenAudioPlayerActionConfig = {
    src: RichTextContentProtocol
    title: RichTextContentProtocol
    epname?: RichTextContentProtocol
    singer?: RichTextContentProtocol
    coverImgUrl?: RichTextContentProtocol
    webUrl?: RichTextContentProtocol
}

export type SendMsgDingTalkRobotConfigMsgType = 'text' | 'link' | 'markdown'

export type SendMsgDingTalkRobotConfig = {
    id: string
    msgtype: SendMsgDingTalkRobotConfigMsgType
    text?: {
        editorContent: RichTextContentProtocol
    }
    webhook: string
    secret: string
    at: {
        atContent: RichTextContentProtocol
        // atMobiles?: string[]
        // atUserIds?: string[]
    }
    markdown?: {
        title: string
        text: string
        editorText: RichTextContentProtocol
    }
    link?: {
        editorMessageUrl: RichTextContentProtocol
        editorTitle: RichTextContentProtocol
        // picUrl: string
        picUrl: RichTextContentProtocol
        editorText: RichTextContentProtocol
    }
}

export type OpenLinkActionConfig = {
    // openLink: string
    link?: RichTextContentProtocol
    target?: HTMLAttributeAnchorTarget
}

export type NotificationActionConfig = {
    notificationContent: RichTextContentProtocol // TiptapEditorJSONContent
    notificationStatus: 'SUCCESS' | 'ERROR' | 'INFO'
}

export type CopyActionConfig = {
    copyText: JSONContent
}

export type SendEmailParagraph = {
    id: string
    type: PARAGRAPH_TYPE
    parentNodeId?: string
    richtext?: RichTextContentProtocol
    html?: RichTextContentProtocol
    targetRichtext?: string // html格式
    targetHtml?: string // html格式
}

export type SendEmailBody = {
    editType: EMAIL_EDIT_TYPE
    paragraph: SendEmailParagraph[]
    width: PARAGRAPH_WIDTH_TYPE
    background: BackgroundProtocol
    border: Border
    padding: AutoLayout['padding']
    gap: AutoLayout['gap']
}

export type SendEmailActionConfig = {
    senderAccountType: SenderAccountType
    title: RichTextContentProtocol
    // cc 抄送人
    enableCc: boolean
    cc: EmailParticipant[]
    // 收件人
    recipients: EmailParticipant[]
    // content: RichTextContentProtocol
    body: SendEmailBody
    background: BackgroundProtocol
    padding: AutoLayout['padding']
}

export type StationMessageActionConfig = {
    title: RichTextContentProtocol
    // personList: ApproverUser[]
    addresseeIds: VariableADTvalue[]
    content: RichTextContentProtocol
}

export type DownloadFileActionConfig = {
    fileUrl: VariableADTvalue // TiptapEditorJSONContent
}

export type CallActionConfig = {
    phone: VariableADTvalue
}

export type SendWebSocketActionConfig = {
    eventName: string
    eventValue: string
    args: SubProcessArg[]
}

export type AliyunMessageActionConfig = {
    mobileList: VariableADTvalue
    signatureId: string
    templateId: string
    values: { variableName: string; value: RichTextContentProtocol }[]
}

export type WeChatPayActionConfig = WechatPayConfig

export type WeChatRefundActionConfig = WechatRefundConfig

export type WeChatRefundQueryActionConfig = WechatRefundQueryConfig

export type WechatUploadShippingInfoActionConfig = WechatUploadShippingInfoConfig

export type WechatGetOrderShippingActionConfig = WechatGetOrderShippingConfig

export type WechatGetOrderListActionConfig = WechatGetOrderListConfig

export type WechatFollowWaybillActionConfig = WechatFollowWaybillConfig

export type WeChatProfitSharingActionConfig = WechatProfitSharingConfig

export type WeChatProfitSharingQueryActionConfig = WechatProfitSharingQueryConfig

export type WeChatProfitSharingQueryAmountActionConfig = WechatProfitSharingQueryAmountConfig

export type WeChatAddProfitSharingReceiverActionConfig = {
    paymentAccountId?: string
    // 分账接收方类型 "MERCHANT_ID" | "PERSONAL_OPENID"
    type: WechatProfitSharingTypeEnum
    // 分账平台 "MINI_PROGRAM" | "WECHAT"
    platform: WechatProfitSharingPlatformEnum
    // 分账接收方账号 富文本结构
    account: RichTextContentProtocol
    // 分账接收方全称 富文本结构
    name?: RichTextContentProtocol
    // 与分账方的关系类型 STORE | STAFF | STORE_OWNER | PARTNER | HEADQUARTER | BRAND | DISTRIBUTOR | USER | SUPPLIER | CUSTOM
    relationType: WechatProfitSharingRelationTypeEnum
    // 自定义的分账关系 - 富文本结构
    customRelation?: RichTextContentProtocol
}

export type WeChatDeleteProfitSharingReceiverActionConfig = {
    paymentAccountId?: string
    // 分账接收方类型 "MERCHANT_ID" | "PERSONAL_OPENID"
    type: WechatProfitSharingTypeEnum
    // 分账平台 "MINI_PROGRAM" | "WECHAT"
    platform: WechatProfitSharingPlatformEnum
    // 分账接收方账号 富文本结构
    account: RichTextContentProtocol
}

export type OpenWeChatCustomerServiceActionConfig = {
    // 企业微信corpId
    corpId: RichTextContentProtocol
    // 客服链接url
    url: RichTextContentProtocol
}

export type WechatTemplateMsgActionConfig = WechatTemplateMsgConfig

export type StripeActionConfig = StripePayConfig

export type HandleSubProcessActionConfig = {
    subProcessId: string
    args: SubProcessArg[]
}

export type SearchApiActionConfig = {
    query: RichTextContentProtocol
    numberOfResult: number
    resultType: SearchApiResultType
}

export enum VisualInputType {
    IMAGE = 'IMAGE'
}

export type VisualInput = {
    id: string
    name: string
    type: VisualInputType
    isEdit?: boolean
    value: VariableADTvalue
}

export type LLMActionConfig = {
    model: string
    inputVariables: VisualInput[]
    systemPrompt: RichTextContentProtocol
    userPrompt: RichTextContentProtocol
}

export type ParamExtractionActionConfig = {
    model: string
    input: RichTextContentProtocol
    args: ExtractParams[]
    prompt: RichTextContentProtocol
}

export type RunCodeActionConfig = {
    inputVariables: RunCodeParam[]
    code: string
    args: ArgumentsSchema[]
}

export interface NodeProtocol {
    name: string
    description?: string
    // platform?: PlatformType
}

// export type NodeInputAndOutputType = 'record' | 'args' | 'condition'

// export interface NodeInputAndOutput {
//     type: NodeInputAndOutputType
//     data: { label: string; value: string | boolean; innerType: TypeInstanceMap }[]
// }

export type NodePutOption = {
    id?: string
    label: string
}

export type NodePutData = MakeADT<
    'type',
    {
        TEXT: { value: TextValue } & NodePutOption
        ARRAY: { value: StringArrayValue } & NodePutOption
        NUMBER: { value: NumberValue } & NodePutOption
        DATE: { value: DateValue } & NodePutOption
        BOOL: { value: BoolValue } & NodePutOption
        RICH_TEXT: { value: RichTextValue } & NodePutOption
        NULL: { value: NullValue } & NodePutOption
    }
>

export interface NodeStatusMessage {
    status?: {
        dsId?: string
        status: NodeAndEdgeStatusEnum
        log: string
        executeTime: Date
        // input: { label: string; value: string | boolean; type: string }[][]
        output: NodePutData[][]
    }
}

// --------- configure -------
// 触发
export type CreateRecordTriggerConfigure = Record<'nodeType', TriggerNodeType.CREATE_RECORD_TRIGGER> &
    NodeProtocol & {
        config: CreateRecordTriggerConfig
    }

export type UpdateRecordTriggerConfigure = Record<'nodeType', TriggerNodeType.UPDATE_RECORD_TRIGGER> &
    NodeProtocol & {
        config: UpdateRecordTriggerConfig
    }

export type MatchConditionTriggerConfigure = Record<'nodeType', TriggerNodeType.MATCH_CONDITION_TRIGGER> &
    NodeProtocol & {
        config: MatchConditionTriggerConfig
    }

export type ScheduledTriggerConfigure = Record<'nodeType', TriggerNodeType.SCHEDULED_TRIGGER> &
    NodeProtocol & {
        config: ScheduledTriggerConfig
    }

export type ClickTriggerConfigure = Record<'nodeType', TriggerNodeType.CLICK_TRIGGER> &
    NodeProtocol & {
        config: ClickTriggerConfig
    }

export type VariableTriggerConfigure = Record<'nodeType', TriggerNodeType.ARG_TRIGGER> &
    NodeProtocol & {
        config: VariableTriggerConfig
    }

export type EmptyTriggerConfigure = Record<'nodeType', TriggerNodeType.EMPTY_TRIGGER> &
    NodeProtocol & {
        config: {}
    }
export type WebsocketTriggerConfigure = Record<'nodeType', TriggerNodeType.WEBSOCKET_TRIGGER> &
    NodeProtocol & {
        config: WebsocketTriggerConfig
    }

export type WebhookTriggerConfigure = Record<'nodeType', TriggerNodeType.WEBHOOK_TRIGGER> &
    NodeProtocol & {
        config: WebhookTriggerConfig
    }

// AI
export type LLMActionConfigure = Record<'nodeType', AINodeType.LLM_ACTION> &
    NodeProtocol & {
        config: LLMActionConfig
    }

export type ParamExtractionActionConfigure = Record<'nodeType', AINodeType.PARAM_EXTRACT_ACTION> &
    NodeProtocol & {
        config: ParamExtractionActionConfig
    }

export type RunCodeActionConfigure = Record<'nodeType', AINodeType.CODE_ACTION> &
    NodeProtocol & {
        config: RunCodeActionConfig
    }

// search API
export type GoogleSearchActionConfigure = Record<'nodeType', OtherPlatformNodeType.GOOGLE_SEARCH_ACTION> &
    NodeProtocol & {
        config: SearchApiActionConfig
    }

export type BaiduSearchActionConfigure = Record<'nodeType', OtherPlatformNodeType.BAIDU_SEARCH_ACTION> &
    NodeProtocol & {
        config: SearchApiActionConfig
    }

export type BingSearchActionConfigure = Record<'nodeType', OtherPlatformNodeType.BING_SEARCH_ACTION> &
    NodeProtocol & {
        config: SearchApiActionConfig
    }

// 数据
export type CreateSingleRecordActionConfigure = Record<'nodeType', ActionDataNodeType.CREATE_SINGLE_RECORD_ACTION> &
    NodeProtocol & {
        config: CreateSingleRecordActionConfig
    }

export type CreateRecordActionConfigure = Record<'nodeType', ActionDataNodeType.CREATE_RECORD_ACTION> &
    NodeProtocol & {
        config: CreateRecordActionConfig
    }

export type UpdateRecordActionConfigure = Record<'nodeType', ActionDataNodeType.UPDATE_RECORD_ACTION> &
    NodeProtocol & {
        config: UpdateRecordActionConfig
    }

export type FindSingleRecordActionConfigure = Record<'nodeType', ActionDataNodeType.FIND_SINGLE_RECORD_ACTION> &
    NodeProtocol & {
        config: FindSingleRecordActionConfig
    }

export type FindRecordActionConfigure = Record<'nodeType', ActionDataNodeType.FIND_RECORD_ACTION> &
    NodeProtocol & {
        config: FindRecordActionConfig
    }

export type DeleteRecordActionConfigure = Record<'nodeType', ActionDataNodeType.DELETE_RECORD> &
    NodeProtocol & {
        config: DeleteRecordActionConfig
    }

export type ImportExcelActionConfigure = Record<'nodeType', ActionDataNodeType.IMPORT_EXCEL> &
    NodeProtocol & {
        config: ImportExcelActionConfig
    }

export type BatchImportFileActionConfigure = Record<'nodeType', ActionDataNodeType.BATCH_IMPORT_FILE> &
    NodeProtocol & {
        config: BatchImportFileActionConfig
    }

// ---集成---
export type SendMsgWxRobotActionConfigure = Record<'nodeType', OtherPlatformNodeType.WX_ROBOT_ACTION> &
    NodeProtocol & {
        config: SendMsgWxRootConfig
    }

export type SendMsgDingTalkRobotActionConfigure = Record<'nodeType', OtherPlatformNodeType.DINGTALK_ROBOT_ACTION> &
    NodeProtocol & {
        config: SendMsgDingTalkRobotConfig
    }

export type AliyunMessageActionConfigure = Record<'nodeType', OtherPlatformNodeType.ALIYUN_MESSAGE_ACTION> &
    NodeProtocol & {
        config: AliyunMessageActionConfig
    }

export type WeChatPayActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_PAY> &
    NodeProtocol & {
        config: WeChatPayActionConfig
    }

export type WeChatRefundActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_REFUND> &
    NodeProtocol & {
        config: WeChatRefundActionConfig
    }

export type WeChatRefundQueryActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_REFUND_QUERY> &
    NodeProtocol & {
        config: WeChatRefundQueryActionConfig
    }

export type WeChatProfitSharingActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_PROFIT_SHARING> &
    NodeProtocol & {
        config: WeChatProfitSharingActionConfig
    }

export type WeChatProfitSharingQueryActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY> &
    NodeProtocol & {
        config: WeChatProfitSharingQueryActionConfig
    }

export type WeChatProfitSharingQueryAmountActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT> &
    NodeProtocol & {
        config: WeChatProfitSharingQueryAmountActionConfig
    }

export type WeChatAddProfitSharingReceiverActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_ADD_RECEIVER> &
    NodeProtocol & {
        config: WeChatAddProfitSharingReceiverActionConfig
    }

export type WeChatDeleteProfitSharingReceiverActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_DELETE_RECEIVER> &
    NodeProtocol & {
        config: WeChatDeleteProfitSharingReceiverActionConfig
    }

export type OpenWeChatCustomerServiceActionConfigure = Record<'nodeType', OtherPlatformNodeType.OPEN_WECHAT_CUSTOMER_SERVICE> &
    NodeProtocol & {
        config: OpenWeChatCustomerServiceActionConfig
    }

export type StripeActionConfigure = Record<'nodeType', OtherPlatformNodeType.STRIPE> &
    NodeProtocol & {
        config: StripeActionConfig
    }

export type WechatTemplateMessageActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_TEMPLATE_MSG_ACTION> &
    NodeProtocol & {
        config: WechatTemplateMsgActionConfig
    }

export type WechatSubscriptionMessageActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_SUBSCRIPTION_MSG_ACTION> &
    NodeProtocol & {
        config: WechatSubscriptionMessageConfig
    }

export type WechatSubscriptionMessageNotificationActionConfigure = Record<
    'nodeType',
    OtherPlatformNodeType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION
> &
    NodeProtocol & {
        config: WechatSubscriptionMessageNotificationsConfig
    }

export type WeChatOpenLocationActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_OPEN_LOCATION_ACTION> &
    NodeProtocol & {
        config: WechatOpenLocationActionConfig
    }

export type WechatNavigateToMiniProgramActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_NAVIGATE_TO_MINI_PROGRAM> &
    NodeProtocol & {
        config: WechatNavigateToMiniProgramActionConfig
    }

export type WechatOpenChannelsActivityActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_OPEN_CHANNELS_ACTIVITY> &
    NodeProtocol & {
        config: WechatOpenChannelsActivityActionConfig
    }

export type WechatGenerateMiniProgramCodeActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE> &
    NodeProtocol & {
        config: WechatGenerateMiniProgramCodeActionConfig
    }

export type WechatUploadShippingInfoActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO> &
    NodeProtocol & {
        config: WechatUploadShippingInfoActionConfig
    }

export type WechatGetOrderShippingActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING> &
    NodeProtocol & {
        config: WechatGetOrderShippingActionConfig
    }

export type WechatGetOrderListActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_GET_ORDER_LIST> &
    NodeProtocol & {
        config: WechatGetOrderListActionConfig
    }

export type WechatFollowWaybillActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL> &
    NodeProtocol & {
        config: WechatFollowWaybillActionConfig
    }

export type WechatOpenAudioPlayerActionConfigure = Record<'nodeType', OtherPlatformNodeType.WECHAT_OPEN_AUDIO_PLAYER> &
    NodeProtocol & {
        config: WechatOpenAudioPlayerActionConfig
    }

export type SendEmailActionConfigure = Record<'nodeType', OtherPlatformNodeType.SEND_EMAIL_ACTION> &
    NodeProtocol & {
        config: SendEmailActionConfig
    }

// ---流程控制---
export type LoopNodeActionConfigure = Record<'nodeType', FlowControlNodeType.LOOP_ACTION> &
    NodeProtocol & {
        config: LoopNodeActionConfig
    }

export type HandleSubProcessActionConfigure = Record<'nodeType', FlowControlNodeType.HANDLE_SUB_PROCESS> &
    NodeProtocol & {
        config: HandleSubProcessActionConfig
    }

export type ConditionConfigure = Record<'nodeType', FlowControlNodeType.CONDITION> &
    NodeProtocol & {
        config: ConditionConfig
    }

export type SendWebSocketActionConfigure = Record<'nodeType', FlowControlNodeType.SEND_WEBSOCKET> &
    NodeProtocol & {
        config: SendWebSocketActionConfig
    }

// ---通知---
export type StationMessageActionConfigure = Record<'nodeType', NoticeNodeType.STATION_MESSAGE_ACTION> &
    NodeProtocol & {
        config: StationMessageActionConfig
    }

// 操作
export type OpenPageActionConfigure = Record<'nodeType', OperatorNodeType.OPEN_PAGE> &
    NodeProtocol & {
        config: OpenPageActionConfig
    }

export type OpenLinkActionConfigure = Record<'nodeType', OperatorNodeType.OPEN_LINK> &
    NodeProtocol & {
        config: OpenLinkActionConfig
    }

// /** @deprecated */
// export type OpenFormPageActionConfigure = Record<'nodeType', OperatorNodeType.OPEN_FORM_PAGE> & NodeProtocol & {
//     config: OpenFormPageActionConfig
// }

// /** @deprecated */
// export type OpenRecordPageActionConfigure = Record<'nodeType', OperatorNodeType.OPEN_RECORD_PAGE> & NodeProtocol & {
//     config: OpenRecordPageActionConfig
// }

// /** @deprecated */
// export type OpenRecordEditPageActionConfigure = Record<'nodeType', OperatorNodeType.OPEN_RECORD_EDIT_PAGE> & NodeProtocol & {
//     config: OpenRecordEditPageActionConfig
// }

export type NotificationActionConfigure = Record<'nodeType', OperatorNodeType.NOTIFICATION> &
    NodeProtocol & {
        config: NotificationActionConfig
    }

export type CopyActionConfigure = Record<'nodeType', OperatorNodeType.COPY> &
    NodeProtocol & {
        config: CopyActionConfig
    }

export type DownloadFileActionConfigure = Record<'nodeType', OperatorNodeType.DOWNLOAD_FILE> &
    NodeProtocol & {
        config: DownloadFileActionConfig
    }

export type GoBackActionConfigure = Record<'nodeType', OperatorNodeType.GO_BACK> &
    NodeProtocol & {
        config: {}
    }

export type RefreshPageActionConfigure = Record<'nodeType', OperatorNodeType.REFRESH_PAGE> &
    NodeProtocol & {
        config: {}
    }

export type CloseFloatActionConfigure = Record<'nodeType', OperatorNodeType.CLOSE_FLOAT> &
    NodeProtocol & {
        config: {}
    }

export type RefreshUserPermissionsActionConfigure = Record<'nodeType', OperatorNodeType.REFRESH_USER_PERMISSIONS> &
    NodeProtocol & {
        config: {}
    }

export type CallActionConfigure = Record<'nodeType', OperatorNodeType.CALL> &
    NodeProtocol & {
        config: CallActionConfig
    }

// end
export type EndConfigure = Record<'nodeType', EndNodeType.END> &
    NodeProtocol & {
        config?: EndConfig
    }

export type TriggerNodeConfigure =
    | CreateRecordTriggerConfigure
    | UpdateRecordTriggerConfigure
    | MatchConditionTriggerConfigure
    | ScheduledTriggerConfigure
    | ClickTriggerConfigure
    | VariableTriggerConfigure
    | EmptyTriggerConfigure
    | WebsocketTriggerConfigure
    | WebhookTriggerConfigure

export type ActionNodeConfigure =
    // 数据处理
    | CreateSingleRecordActionConfigure
    | CreateRecordActionConfigure
    | UpdateRecordActionConfigure
    | FindSingleRecordActionConfigure
    | FindRecordActionConfigure
    | DeleteRecordActionConfigure
    | ImportExcelActionConfigure
    | BatchImportFileActionConfigure
    // 流程控制
    | LoopNodeActionConfigure
    // 集成
    | SendMsgWxRobotActionConfigure
    | SendMsgDingTalkRobotActionConfigure
    | SendEmailActionConfigure
    | StationMessageActionConfigure
    // 集成(新增)
    | GoogleSearchActionConfigure
    | BaiduSearchActionConfigure
    | BingSearchActionConfigure
    | RunCodeActionConfigure
    | ParamExtractionActionConfigure
    | LLMActionConfigure
    // 操作
    | OpenPageActionConfigure
    | OpenLinkActionConfigure
    // | OpenFormPageActionConfigure
    // | OpenRecordPageActionConfigure
    | NotificationActionConfigure
    | CopyActionConfigure
    | DownloadFileActionConfigure
    | AliyunMessageActionConfigure
    | CallActionConfigure
    | SendWebSocketActionConfigure
    // 微信商户号
    | WeChatPayActionConfigure
    | WeChatRefundActionConfigure
    | WeChatRefundQueryActionConfigure
    | WeChatProfitSharingActionConfigure
    | WeChatProfitSharingQueryActionConfigure
    | WeChatProfitSharingQueryAmountActionConfigure
    | WeChatAddProfitSharingReceiverActionConfigure
    | WeChatDeleteProfitSharingReceiverActionConfigure
    | WechatTemplateMessageActionConfigure
    | OpenWeChatCustomerServiceActionConfigure
    | WechatOpenAudioPlayerActionConfigure
    // 小程序
    | WechatSubscriptionMessageActionConfigure
    | WechatSubscriptionMessageNotificationActionConfigure
    | WeChatOpenLocationActionConfigure
    | WechatNavigateToMiniProgramActionConfigure
    | WechatOpenChannelsActivityActionConfigure
    | WechatGenerateMiniProgramCodeActionConfigure
    | WechatUploadShippingInfoActionConfigure
    | WechatGetOrderShippingActionConfigure
    | WechatGetOrderListActionConfigure
    | WechatFollowWaybillActionConfigure
    // stripe支付
    | StripeActionConfigure
    | HandleSubProcessActionConfigure
    | GoBackActionConfigure
    | RefreshPageActionConfigure
    | CloseFloatActionConfigure
    | RefreshUserPermissionsActionConfigure

export type ConditionNodeConfigure = ConditionConfigure

export type EndNodeConfigure = EndConfigure

export type NodeConfigure = TriggerNodeConfigure | ActionNodeConfigure | ConditionNodeConfigure | EndNodeConfigure

export type BaseFlowNode = Node & {
    connectedSourceHandleIds?: string[]
}

export enum ClassNode {
    TRIGGER = 'TRIGGER',
    ACTION = 'ACTION',
    CONDITION = 'CONDITION',
    END = 'END'
}

export type TriggerFlowNode<T = TriggerNodeConfigure> = BaseFlowNode & {
    type: ClassNode.TRIGGER
    data: T & NodeStatusMessage
}

export type ActionFlowNode<T = ActionNodeConfigure> = BaseFlowNode & {
    type: ClassNode.ACTION
    data: T & NodeStatusMessage
}

export type ConditionFlowNode<T = ConditionNodeConfigure> = BaseFlowNode & {
    type: ClassNode.CONDITION
    data: T & NodeStatusMessage
}

export type EndFlowNode<T = EndConfigure> = BaseFlowNode & {
    type: ClassNode.END
    data: T & NodeStatusMessage
}

export interface OtherFlowNode {
    type?: ClassNode
}

export interface BaseFlowEdge {
    id: string
    source: string
    target: string
    sourceHandle?: string
    data?: {
        isNodeHovering?: boolean
    }
    selected?: boolean
    animated?: boolean
}

export enum ClassEdge {
    Common = 'common'
}

export interface CommonFlowEdge extends BaseFlowEdge {
    type: ClassEdge.Common
}

export interface OtherFlowEdge extends BaseFlowEdge {
    type?: ClassEdge
}

export type FlowNode<T = NodeConfigure> = TriggerFlowNode<T> | ActionFlowNode<T> | ConditionFlowNode<T> | EndFlowNode

export type FlowEdge = CommonFlowEdge

export type TriggerDataSourceNodeConfig =
    | CreateRecordTriggerConfig
    | UpdateRecordTriggerConfig
    | MatchConditionTriggerConfig
    | InitiateApproveTriggerConfig
    | CreateRecordActionConfig

export enum WorkflowType {
    'AUTOMATION' = 'automation',
    'ACTION' = 'action',
    'SUB_PROCESS' = 'subProcess'
}

export enum WorkflowRunType {
    SEQUENTIAL = 'SEQUENTIAL', // 顺序执行
    STRICT_SEQUENTIAL = 'STRICT_SEQUENTIAL', // 严格串行执行
    PARALLEL = 'PARALLEL' // 并行执行
}

export type Workflow = {
    id: string
    type: WorkflowType
    runType: WorkflowRunType
    name: string
    description?: string
    unConfigured: boolean
    changed?: boolean
    content: {
        nodes: FlowNode[]
        edges: FlowEdge[]
    }
    updatedTime?: Date
    updatedBy?: string
    createdTime?: Date
    createdBy?: string
    enabled: boolean
    errorMap?: Record<string, boolean>
}

export enum FlowRunStatus {
    'PENDING' = 'PENDING',
    'RUNNING' = 'RUNNING',
    'SUCCESS' = 'SUCCESS',
    'FAILED' = 'FAILED'
}

export type WorkflowLog = {
    instanceId: string
    state: FlowRunStatus
    content: Workflow['content']
}

export type UpdateWorkflowLogPayload = {
    /**
     * 工作流id 必传
     */
    workflowId: string
    /**
     * 工作流实整体运行状态枚举值，有 RUNNING, SUCCESS , FAILED，必传
     */
    status: FlowRunStatus
    /**
     * 工作流实例id，必传
     */
    workflowInstanceId: string
    /**
     * 节点id，必传
     */
    nodeId: string
    nodeStatus: {
        /**
         * 节点状态数据结构，与自动化的节点状态结构一致 ，必传, // 有 RUNNING, SUCCESS , FAILED，必传
         */
        status: NodeAndEdgeStatusEnum
        input: { label: string; type: string; value: string }[]
        output: NodePutData[][]
    }
}

export type ConditionOutput = { id: string; name: string; isPass: boolean }

export type FlowNodeResultADT = MakeADT<
    'type',
    {
        args: { success: true; data?: SubProcessResolvedArg[] } | { success: false }
        record: { success: true; data?: RecordLikeProtocol } | { success: false }
        condition: { success: true; data: ConditionOutput[] } | { success: false }
        interrupted: { success: true }
    }
>

export type ActionResultADT = MakeADT<
    'type',
    {
        args: { success: true; data?: SubProcessResolvedArg[] } | { success: false }
        record: { success: true; data?: RecordLikeProtocol } | { success: false }
        interrupted: { success: true }
        continue: { success: true }
    }
>

export type NodeInvokeResult<E extends AnyObject> = UpdateWorkflowLogPayload & {
    runtimeParams: E
    result: FlowNodeResultADT
}

export type UsedWorkflow = {
    workflowId: string
    workflowType: WorkflowType
    workflowName: string
    nodeId: string
    nodeName: string
    nodeType: NodeTypes
}

// --------- config -------
export type NodeOption = {
    value: NodeTypes
    label: string
    icon?: string
    color?: string
    // describe?: string
    desc?: string
    subType?: SenderAccountType
    auth: WorkflowType[]
    background?: string
}

export type NodeGroupOption = {
    label: string
    type?: string
    unitType?: OtherPlatformType
    children: NodeOption[]
}

export type AiModule = {
    configurationComplete?: boolean
    modules: { label: string; value: string }[]
    type: AiPlatformType
    system?: boolean
}

export type AiModuleIconType = {
    label: string
    icon: string
    color?: string
    value: AiPlatformType
}
